'use client'

import { useState } from 'react'
import { Bell, Volume2, X } from 'lucide-react'
import { Button } from '@elmnts/ui/button'
import { Checkbox } from '@elmnts/ui/checkbox'
import { Label } from '@elmnts/ui/label'
import { activateNotifications } from '@/lib/notifications/activation'
import { dismissBanner } from '@/lib/notifications/prefs'

interface PermissionBannerProps {
  onClose: () => void
}

/**
 * Pedido único de "como quer ser avisado". Serve a dois propósitos: liga as
 * prefs e, principalmente, **é o gesto do usuário** que destrava o autoplay de
 * áudio e o prompt de permissão do browser.
 */
export function PermissionBanner({ onClose }: PermissionBannerProps) {
  const [wantSound, setWantSound] = useState(true)
  const [wantBrowser, setWantBrowser] = useState(true)
  const [working, setWorking] = useState(false)

  const handleActivate = async () => {
    setWorking(true)
    try {
      // Toda a ordem crítica (permissão antes do await do áudio) mora em
      // `lib/notifications/activation` — a tela de conta usa o mesmo módulo.
      await activateNotifications({ wantSound: wantSound, wantBrowser: wantBrowser })
      dismissBanner()
      onClose()
    } finally {
      setWorking(false)
    }
  }

  const handleDismiss = () => {
    dismissBanner()
    onClose()
  }

  return (
    <div className="fixed left-1/2 top-4 z-50 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2">
      <div className="relative space-y-3 rounded-lg border bg-card p-4 shadow-lg">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Fechar"
          className="absolute right-2 top-2 inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>

        <div className="pr-6">
          <h3 className="text-sm font-semibold">Receber notificações?</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Escolha como você quer ser avisado quando algo acontecer.
          </p>
        </div>

        <div className="space-y-2">
          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox
              id="banner-sound"
              checked={wantSound}
              onCheckedChange={(v) => setWantSound(v === true)}
              className="mt-0.5"
            />
            <span className="min-w-0 flex-1">
              <Label
                htmlFor="banner-sound"
                className="flex cursor-pointer items-center gap-1.5 text-sm font-medium"
              >
                <Volume2 className="size-3.5" /> Som ao receber
              </Label>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Beep curto quando uma notificação nova chega.
              </span>
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox
              id="banner-browser"
              checked={wantBrowser}
              onCheckedChange={(v) => setWantBrowser(v === true)}
              className="mt-0.5"
            />
            <span className="min-w-0 flex-1">
              <Label
                htmlFor="banner-browser"
                className="flex cursor-pointer items-center gap-1.5 text-sm font-medium"
              >
                <Bell className="size-3.5" /> Notificações do navegador
              </Label>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Aviso do sistema quando você estiver em outra aba.
              </span>
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={handleDismiss} disabled={working}>
            Agora não
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleActivate}
            disabled={working || (!wantSound && !wantBrowser)}
          >
            Ativar
          </Button>
        </div>
      </div>
    </div>
  )
}
